import { DndContext, MouseSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import { memo, useCallback, useMemo } from "react";

import type { HotelInfoItemInput } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

import { FormItem } from "@/components/ui/form/_base/form";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";

import { SortableHotelInfoRow } from "./sortable-hotel-info-row";
import { QuestionCardItem } from "../../../../../../_shared/ui/components/system/question/card/card-item";
import { QuestionCardItemsWrapper } from "../../../../../../_shared/ui/components/system/question/card/card-items-wrapper";

type Props = {
	hotelInfo: ForArrayType<HotelInfoItemInput[]>;

	appendHotelInfo: () => void;
	removeHotelInfo: (index: number) => void;
	reorderHotelInfo: (activeIndex: number, overIndex: number) => void;

	changeHotelInfoTitle: (value: string, index: number) => void;
	changeHotelInfoDescription: (value: string, index: number) => void;
};

const toSortableId = (index: number) => `hotel-info-${index}`;

export const QuestionHotelInfo = memo(
	({
		hotelInfo,
		appendHotelInfo,
		removeHotelInfo,
		reorderHotelInfo,
		changeHotelInfoTitle,
		changeHotelInfoDescription,
	}: Props) => {
		const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
		const sensors = useSensors(mouseSensor);

		const sortableIds = useMemo(
			() => hotelInfo.value.map((_, index) => toSortableId(index)),
			[hotelInfo.value],
		);

		const handleDragEnd = useCallback(
			({ active, over }: DragEndEvent) => {
				if (!over || active.id === over.id) return;
				const activeIndex = sortableIds.indexOf(String(active.id));
				const overIndex = sortableIds.indexOf(String(over.id));
				reorderHotelInfo(activeIndex, overIndex);
			},
			[reorderHotelInfo, sortableIds],
		);

		const canReorder = hotelInfo.value.length > 1;

		const guide = `施設からゲストに伝えたい情報を登録しましょう。<br />
	登録した内容は予約前にゲストに表示され、
	予約時に送信されるメールにも表記されます。`;

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={"施設に関する注意事項"}
				guide={guide}
				isEdited={hotelInfo.isEdited}
				hasError={Boolean(hotelInfo.hasError)}
				inputChildren={
					<QuestionCardItemsWrapper appendItem={appendHotelInfo}>
						{canReorder ? (
							<>
								<p className="text-sm text-caption-50">
									左端の並べ替えアイコンをドラッグして、注意事項の表示順を変更できます。
								</p>
								<DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
									<FormItem>
										<SortableContext items={sortableIds}>
											{hotelInfo.value.map((item, index) => (
												<SortableHotelInfoRow
													key={toSortableId(index)}
													sortableId={toSortableId(index)}
													index={index}
													item={item}
													removeHotelInfo={removeHotelInfo}
													changeHotelInfoTitle={changeHotelInfoTitle}
													changeHotelInfoDescription={changeHotelInfoDescription}
												/>
											))}
										</SortableContext>
									</FormItem>
								</DndContext>
							</>
						) : (
							<FormItem>
								{hotelInfo.value.map((item, index) => (
									<QuestionCardItem
										key={`hotel_policy_hotel_info_${index}`}
										removeItem={() => removeHotelInfo(index)}
									>
										<div className="flex flex-col gap-y-10">
											<InputTextField
												label="タイトル"
												value={item.title.value}
												handleChange={(input) => changeHotelInfoTitle(input, index)}
												placeholder="例：施設は禁煙ですか？"
												errorMessage={item.title.error}
											/>
											<TextAreaField
												label="説明"
												value={item.description.value}
												handleChange={(input) => changeHotelInfoDescription(input, index)}
												placeholder="例：全室禁煙となっていますが、喫煙ルームをご用意しております。"
												errorMessage={item.description.error}
											/>
										</div>
									</QuestionCardItem>
								))}
							</FormItem>
						)}
					</QuestionCardItemsWrapper>
				}
			/>
		);
	},
);

QuestionHotelInfo.displayName = "QuestionHotelInfo";
